import { type ReactNode, useEffect, useState } from 'react';

import {
  ADS_DECISION_ACTORS,
  ADS_DECISION_OUTCOMES,
  type AdsDecisionActor,
  type AdsDecisionEntry,
  type AdsDecisionLog,
  type AdsDecisionOutcome,
  type AdsDecisionQuery,
  fetchAdsDecisions,
} from './api.ts';
import { describeError } from './data.tsx';
import { Icon } from './Icon.tsx';
import { localTime } from './schedule.ts';
import { Badge, EmptyState, Facts, Notice, Skeleton, type Tone } from './ui.tsx';

/**
 * The decision log (meta-ads-agent US-016): `ads_decisions` as a timeline.
 * `DecisionEntry` / `DecisionList` are the one way a decision is drawn — the
 * run detail (US-017) and the ad detail (US-022) use them too. The server
 * labels each decision and parses its JSON (`ads/decisions.ts`), so nothing
 * here knows which writer stored which shape.
 */

export const ACTOR_LABEL: Readonly<Record<AdsDecisionActor, string>> = {
  agent: 'Agent',
  system: 'System',
  operator: 'Operator',
};

export const OUTCOME_TONE: Readonly<Record<AdsDecisionOutcome, Tone>> = {
  executed: 'done',
  refused: 'danger',
  failed: 'danger',
  held: 'wait',
};

const ACTOR_ICON = {
  agent: 'bulb',
  system: 'sync',
  operator: 'pencil',
} as const;

export function DecisionList({
  decisions,
  empty = 'No decisions yet.',
}: {
  readonly decisions: readonly AdsDecisionEntry[];
  readonly empty?: ReactNode;
}) {
  if (decisions.length === 0) return <p className="muted">{empty}</p>;
  return (
    <ol className="ads-decisions">
      {decisions.map((decision) => (
        <DecisionEntry key={decision.id} decision={decision} />
      ))}
    </ol>
  );
}

export function DecisionEntry({ decision }: { readonly decision: AdsDecisionEntry }) {
  const problem = decision.outcome === 'refused' || decision.outcome === 'failed';
  const evidence = factItems(decision.evidence);
  const params = factItems(decision.params);
  const detail = factItems(decision.outcomeDetail, problem ? ['message'] : []);
  const hasMore = evidence.length > 0 || params.length > 0 || detail.length > 0 || problem;
  return (
    <li className={`ads-decision ads-decision--${decision.outcome}`}>
      <div className="ads-decision__head">
        <time className="ads-decision__time" dateTime={decision.createdAt}>
          {localTime(decision.createdAt)}
        </time>
        <span className="ads-decision__actor" title={`Made by the ${decision.actor}`}>
          <Icon name={ACTOR_ICON[decision.actor]} />
          {ACTOR_LABEL[decision.actor]}
        </span>
        <Badge tone={OUTCOME_TONE[decision.outcome]}>{decision.outcome}</Badge>
      </div>
      <div className="ads-decision__title">
        <strong>{decision.label}</strong>
        {(decision.targetName ?? decision.targetMetaId) !== null && (
          <span className="muted"> · {decision.targetName ?? decision.targetMetaId}</span>
        )}
      </div>
      {decision.reason !== null && decision.reason !== '' && (
        <p className="ads-decision__reason">{decision.reason}</p>
      )}
      {(decision.ruleId !== null || decision.judgeAfter !== null) && (
        <div className="ads-decision__meta">
          {decision.ruleId !== null && (
            <span>
              <span className="mono">{decision.ruleId}</span>
              {decision.ruleDescription !== null && <> — {decision.ruleDescription}</>}
            </span>
          )}
          {decision.judgeAfter !== null && <span>Judge again after {localTime(decision.judgeAfter)}</span>}
        </div>
      )}
      {hasMore && (
        <details className="disclosure">
          <summary className="disclosure__summary">
            <Icon name="chevron-right" />
            {problem ? `Evidence, params and why it was ${decision.outcome}` : 'Evidence and params'}
          </summary>
          <div className="disclosure__body">
            {problem && (
              <Notice kind="error">
                <strong>{decision.outcome === 'refused' ? 'Refused by a guardrail' : 'Meta answered with an error'}:</strong>{' '}
                {detailMessage(decision.outcomeDetail)}
              </Notice>
            )}
            {evidence.length > 0 && <FactsBlock title="Evidence" items={evidence} />}
            {params.length > 0 && <FactsBlock title="Params" items={params} />}
            {detail.length > 0 && <FactsBlock title={problem ? 'Detail' : 'Result'} items={detail} />}
          </div>
        </details>
      )}
    </li>
  );
}

function FactsBlock({
  title,
  items,
}: {
  readonly title: string;
  readonly items: readonly { label: string; value: ReactNode; mono?: boolean }[];
}) {
  return (
    <div className="ads-decision__facts">
      <h4 className="ads-decision__facts-title">{title}</h4>
      <Facts items={items} />
    </div>
  );
}

/** What a refusal or a Meta error says; the raw JSON when it says nothing readable. */
export function detailMessage(detail: unknown): string {
  if (detail === null || detail === undefined) return 'no detail was stored';
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'object' && 'message' in detail && typeof detail.message === 'string') {
    return detail.message;
  }
  return JSON.stringify(detail);
}

/** `cost_per_booked_call` / `costPerBookedCall` → "Cost per booked call". */
function humanKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/gu, '$1 $2')
    .replace(/[_-]+/gu, ' ')
    .trim()
    .toLowerCase();
  return words === '' ? key : words.charAt(0).toUpperCase() + words.slice(1);
}

/** A JSON value as Facts rows: one per key of an object, else a single "Value". */
function factItems(
  value: unknown,
  skip: readonly string[] = [],
): { label: string; value: ReactNode; mono?: boolean }[] {
  if (value === null || value === undefined) return [];
  if (typeof value !== 'object' || Array.isArray(value)) return [{ label: 'Value', ...factValue(value) }];
  return Object.entries(value as Record<string, unknown>)
    .filter(([key]) => !skip.includes(key))
    .map(([key, one]) => ({ label: humanKey(key), ...factValue(one) }));
}

function factValue(value: unknown): { value: ReactNode; mono?: boolean } {
  if (value === null || value === undefined) return { value: '—' };
  if (typeof value === 'string') return { value };
  if (typeof value === 'number' || typeof value === 'boolean') return { value: String(value), mono: true };
  return { value: <pre className="ads-decision__json">{JSON.stringify(value, null, 2)}</pre> };
}

/* ------------------------------------------------------------ the log tab */

export interface DecisionFilters {
  readonly actor: AdsDecisionActor | '';
  readonly outcome: AdsDecisionOutcome | '';
  readonly kind: string;
  readonly target: string;
  readonly page: number;
}

/** The filters as the URL keeps them: `?actor&outcome&kind&target&page`. */
export function readDecisionFilters(params: URLSearchParams): DecisionFilters {
  const actor = params.get('actor') ?? '';
  const outcome = params.get('outcome') ?? '';
  const page = Number(params.get('page') ?? '1');
  return {
    actor: (ADS_DECISION_ACTORS as readonly string[]).includes(actor) ? (actor as AdsDecisionActor) : '',
    outcome: (ADS_DECISION_OUTCOMES as readonly string[]).includes(outcome) ? (outcome as AdsDecisionOutcome) : '',
    kind: params.get('kind') ?? '',
    target: params.get('target') ?? '',
    page: Number.isInteger(page) && page >= 1 ? page : 1,
  };
}

/**
 * The Decisions tab: four filters and a page of the timeline. The filters are
 * the URL's, so `onChange` writes there and this re-renders from it; a known
 * object's name comes from `knownTargets` (the overview tree), any other one
 * from the log itself.
 */
export function DecisionLogView({
  filters,
  knownTargets,
  onChange,
}: {
  readonly filters: DecisionFilters;
  readonly knownTargets: readonly { readonly metaId: string; readonly label: string }[];
  readonly onChange: (key: keyof DecisionFilters, value: string) => void;
}) {
  const [log, setLog] = useState<AdsDecisionLog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { actor, outcome, kind, target, page } = filters;

  useEffect(() => {
    const controller = new AbortController();
    const query: AdsDecisionQuery = {
      page,
      ...(actor === '' ? {} : { actor }),
      ...(outcome === '' ? {} : { outcome }),
      ...(kind === '' ? {} : { kind }),
      ...(target === '' ? {} : { target }),
    };
    fetchAdsDecisions(query, controller.signal)
      .then((next) => {
        setLog(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) setError(describeError(cause));
      });
    return () => {
      controller.abort();
    };
  }, [actor, outcome, kind, target, page]);

  const targets = new Map(knownTargets.map((one) => [one.metaId, one.label]));
  for (const one of log?.targets ?? []) {
    if (!targets.has(one.metaId)) targets.set(one.metaId, one.name ?? one.metaId);
  }
  if (target !== '' && !targets.has(target)) targets.set(target, target);
  const kinds = new Map((log?.kinds ?? []).map((one) => [one.kind, one.label]));
  if (kind !== '' && !kinds.has(kind)) kinds.set(kind, kind);

  const pages = log === null ? 1 : Math.max(1, Math.ceil(log.total / log.pageSize));
  const filtered = actor !== '' || outcome !== '' || kind !== '' || target !== '';

  return (
    <>
      <div className="toolbar">
        <select
          className="field__input field__input--inline"
          aria-label="Actor"
          value={actor}
          onChange={(event) => {
            onChange('actor', event.target.value);
          }}
        >
          <option value="">Every actor</option>
          {ADS_DECISION_ACTORS.map((one) => (
            <option key={one} value={one}>
              {ACTOR_LABEL[one]}
            </option>
          ))}
        </select>
        <select
          className="field__input field__input--inline"
          aria-label="Outcome"
          value={outcome}
          onChange={(event) => {
            onChange('outcome', event.target.value);
          }}
        >
          <option value="">Every outcome</option>
          {ADS_DECISION_OUTCOMES.map((one) => (
            <option key={one} value={one}>
              {one.charAt(0).toUpperCase() + one.slice(1)}
            </option>
          ))}
        </select>
        <select
          className="field__input field__input--inline"
          aria-label="Kind"
          value={kind}
          onChange={(event) => {
            onChange('kind', event.target.value);
          }}
        >
          <option value="">Every kind</option>
          {[...kinds].map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          className="field__input field__input--inline"
          aria-label="Target"
          value={target}
          onChange={(event) => {
            onChange('target', event.target.value);
          }}
        >
          <option value="">Every campaign, ad set and ad</option>
          {[...targets].map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        {filtered && (
          <button
            type="button"
            className="button button--quiet"
            onClick={() => {
              onChange('actor', '');
              onChange('outcome', '');
              onChange('kind', '');
              onChange('target', '');
            }}
          >
            Clear filters
          </button>
        )}
      </div>
      {error !== null && <Notice kind="error">{error}</Notice>}
      {log === null ? (
        error === null && <Skeleton lines={6} />
      ) : log.total === 0 ? (
        <EmptyState icon="list" title={filtered ? 'No decision matches these filters' : 'No decisions yet'}>
          {filtered
            ? 'Clear a filter to see more.'
            : 'Every change the agent, the pacing check or you make is logged here, and so is every hold.'}
        </EmptyState>
      ) : (
        <>
          <p className="muted">
            {log.total} {log.total === 1 ? 'decision' : 'decisions'}, newest first
            {pages > 1 && ` · page ${String(log.page)} of ${String(pages)}`}
          </p>
          <DecisionList decisions={log.decisions} />
          {pages > 1 && (
            <div className="toolbar">
              <button
                type="button"
                className="button"
                disabled={log.page <= 1}
                onClick={() => {
                  onChange('page', String(log.page - 1));
                }}
              >
                Newer
              </button>
              <button
                type="button"
                className="button"
                disabled={log.page >= pages}
                onClick={() => {
                  onChange('page', String(log.page + 1));
                }}
              >
                Older
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}
